import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { resolveContactIdentity } from '../../../api/contact';

/**
 * Makes any person shown in a CRM list clickable so it opens their contact
 * profile page (`/contacts/:id`) — the counterpart to {@link ContactNotesButton}.
 *
 * Many list rows show *portal buyers* / enquiry rows that aren't CRM contacts
 * yet, so when there's no `contactId` we resolve (get-or-create) a Contact by
 * email/buyer id on the backend, then navigate. Rows that already carry a
 * `contactId` navigate immediately with no API call.
 *
 * Props:
 *   identity   { contactId?, buyerId?, email?, name?, phone? } — at least one of
 *              contactId / email / buyerId is needed to resolve a profile.
 *   children   the content to make clickable (usually the contact's name).
 *   className  classes for the clickable element (defaults to a teal link look).
 *   title      tooltip (defaults to "View contact profile").
 *   stop       stopPropagation on click so it doesn't trigger a parent row
 *              handler (default true).
 *   returnTo   optional path the profile page's Back arrow should return to
 *              (e.g. the property's enquiries tab). Without it the profile Back
 *              falls back to the generic Contacts list — which is only right
 *              when the click actually came from that list.
 *   returnLabel optional human label for that destination, shown in the Back
 *              tooltip ("Back to <label>").
 */
const ContactLink = ({
  identity = {},
  children,
  className,
  title = 'View contact profile',
  stop = true,
  returnTo,
  returnLabel,
}) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  // Carry the origin through react-router location state so the profile page's
  // Back arrow returns here instead of the generic /contacts list.
  const navOptions = returnTo ? { state: { returnTo, returnLabel } } : undefined;

  const canResolve = !!(
    identity.contactId ||
    identity.buyerId ||
    (identity.email && identity.email.includes('@'))
  );

  const handleClick = useCallback(
    async (e) => {
      if (stop) e?.stopPropagation?.();
      e?.preventDefault?.();
      if (loading) return;

      // Already a CRM contact — go straight there.
      if (identity.contactId) {
        navigate(`/contacts/${identity.contactId}`, navOptions);
        return;
      }
      if (!canResolve) {
        toast.error('No email on file for this person — add them as a contact first.');
        return;
      }
      setLoading(true);
      try {
        const res = await resolveContactIdentity(identity);
        const contactId = res?.data?.contact_id;
        if (contactId) {
          navigate(`/contacts/${contactId}`, navOptions);
        } else {
          toast.error("Couldn't open this contact's profile.");
        }
      } catch (err) {
        toast.error("Couldn't open this contact's profile.");
      } finally {
        setLoading(false);
      }
    },
    [identity, canResolve, loading, navigate, stop, returnTo, returnLabel]
  );

  return (
    <button
      type="button"
      onClick={handleClick}
      title={title}
      disabled={loading}
      className={
        className ||
        'inline-flex items-center gap-1 text-left font-medium text-[#3a99a3] hover:text-[#55C5D0] hover:underline transition-colors disabled:opacity-60'
      }
    >
      {children}
      {loading && <Loader2 className="h-3 w-3 animate-spin flex-shrink-0" />}
    </button>
  );
};

export default ContactLink;
